import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  useTheme,
  useMediaQuery,
  GlobalStyles
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import UserManagementPanel from '../components/settings/UserManagementPanel';
import CategoryManagementPanel from '../components/settings/CategoryManagementPanel';
import AuditLogPanel from '../components/settings/AuditLogPanel';
import NotificationPanel from '../components/settings/NotificationPanel';

import AdminUnlockRequestsPanel from '../components/settings/AdminUnlockRequestsPanel';
import SnapshotManagementPanel from '../components/settings/SnapshotManagementPanel';
import DataIntegrityPanel from '../components/settings/DataIntegrityPanel';

const Settings = () => {
  const { currentUser, updateUserProfile, updatePassword, loading } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();

  // Component helper cho section header - Consistent typography scale
  const SectionHeader = ({ icon, title, subtitle }) => (
    <Box sx={{ 
      mb: 4,
      textAlign: 'left',
      width: '100%'
    }}>
      <Typography variant="h5" sx={{ 
        color: 'text.primary', 
        mb: 1,
        fontWeight: 600,
        display: 'flex', 
        alignItems: 'center',
        gap: 1.5,
        fontSize: '1.5rem',
        lineHeight: 1.3
      }}>
        <Box component="span" sx={{ 
          fontSize: '1.2em',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '1.5em'
        }}>
          {icon}
        </Box>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ 
          fontSize: '0.95rem',
          fontWeight: 400,
          opacity: 0.8,
          ml: '2.7rem',
          lineHeight: 1.4
        }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );

  // Component helper cho form section - Consistent spacing
  const FormSection = ({ children, ...props }) => (
    <Card elevation={0} sx={{ 
      backgroundColor: 'background.paper', 
      border: '1px solid', 
      borderColor: 'divider', 
      borderRadius: 2,
      width: '100%',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      transition: 'box-shadow 0.2s ease-in-out',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
      },
      ...props.sx
    }}>
      <CardContent sx={{ 
        p: 4,
        '&:last-child': { pb: 4 },
        '& .MuiTextField-root': {
          mb: 0
        },
        '& .MuiGrid-item': {
          display: 'flex',
          flexDirection: 'column'
        }
      }}>
        {children}
      </CardContent>
    </Card>
  );
  
  const [activeTab, setActiveTab] = useState(() => {
    // Check URL parameters for tab
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam === 'unlock-requests') {
      return 5; // Tab index for unlock requests
    }
    if (tabParam === 'snapshots') {
      return 6; // Tab index for snapshot management
    }
    return 0;
  });
  const [loadingState, setLoadingState] = useState(false);
  
  // Khởi tạo isAdmin dựa trên dữ liệu currentUser ngay từ đầu
  const [isAdmin, setIsAdmin] = useState(() => {
    if (currentUser && (currentUser.role_name === 'ADMIN' || currentUser.role_name === 'SYSTEM_ADMIN')) {
      return true;
    }
    return false;
  });
  const [isManager, setIsManager] = useState(() => {
    if (currentUser && (currentUser.role_name === 'ADMIN' || currentUser.role_name === 'SYSTEM_ADMIN' || currentUser.role_name.includes('MANAGER'))) {
      return true;
    }
    return false;
  });
  const [loadingUserData, setLoadingUserData] = useState(true);
  
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    re_new_password: '',
  });
  
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  
  useEffect(() => {
    if (currentUser) {
      setProfileData({
        first_name: currentUser.first_name || '',
        last_name: currentUser.last_name || '',
        email: currentUser.email || '',
      });
      setIsAdmin(currentUser.role_name === 'ADMIN' || currentUser.role_name === 'SYSTEM_ADMIN');
      setIsManager(currentUser.role_name === 'ADMIN' || currentUser.role_name === 'SYSTEM_ADMIN' || currentUser.role_name.includes('MANAGER'));
      setLoadingUserData(false);
    } else {
      setLoadingUserData(true);
      setIsAdmin(false);
      setIsManager(false);
    }
  }, [currentUser, setProfileData]);

  useEffect(() => {
    const updateAdminStatus = () => {
      if (currentUser) {
        setIsAdmin(currentUser.role_name === 'ADMIN' || currentUser.role_name === 'SYSTEM_ADMIN');
        setIsManager(currentUser.role_name === 'ADMIN' || currentUser.role_name === 'SYSTEM_ADMIN' || currentUser.role_name.includes('MANAGER'));
      } else {
        setIsAdmin(false);
        setIsManager(false);
      }
    };
    updateAdminStatus();
  }, [currentUser]);

  // Handle URL parameters for tab navigation
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    
    if (tabParam) {
      const tabMap = {
        'profile': 0,
        'password': 1,
        'users': 2,
        'categories': 3,
        'audit': 4,
        'unlock-requests': 5,
        'notifications': 6,
        'snapshots': 7,
        'data-integrity': 8
      };
      
      const tabIndex = tabMap[tabParam];
      if (tabIndex !== undefined) {
        setActiveTab(tabIndex);
      }
    }
  }, [location.search]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setErrorMessage('');
    setSuccessMessage('');
  };
  
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoadingState(true);
    setErrorMessage('');
    setSuccessMessage('');
    setOpenSnackbar(false);
    
    try {
      await updateUserProfile(profileData);
      setSuccessMessage('Profile updated successfully!');
      setOpenSnackbar(true);
    } catch (err) {
      console.error('Profile update error:', err);
      const errorMessage = err.response?.data?.error || 
                           err.response?.data?.detail || 
                           'An error occurred while updating profile. Please try again.';
      setErrorMessage(errorMessage);
      setOpenSnackbar(true);
    } finally {
      setLoadingState(false);
    }
  };
  
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoadingState(true);
    setErrorMessage('');
    setSuccessMessage('');
    setOpenSnackbar(false);
    
    if (passwordData.new_password !== passwordData.re_new_password) {
      setErrorMessage('New passwords do not match');
      setOpenSnackbar(true);
      setLoadingState(false);
      return;
    }
    
    if (passwordData.new_password.length < 8) {
      setErrorMessage('Password must be at least 8 characters long');
      setOpenSnackbar(true);
      setLoadingState(false);
      return;
    }
    
    try {
      await updatePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      
      setSuccessMessage('Password changed successfully');
      setOpenSnackbar(true);
      setPasswordData({
        current_password: '',
        new_password: '',
        re_new_password: '',
      });
    } catch (err) {
      console.error('Password change error:', err);
      const errorMessage = err.response?.data?.error || 
                           err.response?.data?.detail || 
                           'An error occurred while changing password. Please try again.';
      setErrorMessage(errorMessage);
      setOpenSnackbar(true);
    } finally {
      setLoadingState(false);
    }
  };
  
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setSuccessMessage('');
    setErrorMessage('');
  };

  const showSnackbar = (message, severity = 'success') => {
    if (severity === 'success') {
      setSuccessMessage(message);
      setErrorMessage('');
    } else {
      setErrorMessage(message);
      setSuccessMessage('');
    }
    setOpenSnackbar(true);
  };
  
  if (loading || !currentUser || loadingUserData) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: 'background.default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <GlobalStyles
        styles={{
          'html, body, #root': {
            overflowX: 'hidden !important',
            maxWidth: '100vw !important',
            margin: 0,
            padding: 0,
          },
          '*': {
            boxSizing: 'border-box !important',
          },
          '.MuiContainer-root': {
            paddingLeft: '16px !important',
            paddingRight: '16px !important',
            maxWidth: '100% !important',
            width: '100% !important',
            margin: '0 auto !important',
          },
          '.MuiGrid-container': {
            width: '100% !important',
            margin: '0 !important',
          },
          '.MuiGrid-item': {
            maxWidth: '100% !important',
          },
          '.MuiPaper-root': {
            maxWidth: '100% !important',
          },
        }}
      />
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: 'background.default',
        py: { xs: 2, sm: 3 },
        px: 0,
        overflow: 'hidden',
        overflowY: 'auto',
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box'
      }}>
      <Container maxWidth="xl" sx={{ 
        px: { xs: 2, sm: 3, md: 4 },
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto',
        boxSizing: 'border-box'
      }}>
        {/* Header Section */}
        <Box sx={{ mb: { xs: 3, sm: 4, md: 5 } }}>
          <Typography variant="h3" gutterBottom sx={{ 
            fontWeight: 600, 
            color: 'text.primary',
            mb: 1,
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
          }}>
            Settings
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ 
            fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
            fontWeight: 400,
            opacity: 0.8
          }}>
            Manage your account, preferences, and system administration
          </Typography>
        </Box>
        
        {/* Main Settings Card */}
        <Paper elevation={0} sx={{ 
          borderRadius: { xs: 2, sm: 3, md: 4 },
          overflow: 'hidden',
          overflowX: 'hidden',
          border: '1px solid',
          borderColor: 'grey.200',
          backgroundColor: 'background.paper',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          margin: 0
        }}>
          {/* Tab Navigation */}
          <Box sx={{ 
            backgroundColor: 'grey.50',
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            px: 0,
            py: 0,
            width: '100%',
            overflow: 'hidden'
          }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange} 
              aria-label="settings tabs" 
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                width: '100%',
                minHeight: { xs: 48, sm: 56, md: 60 },
                '& .MuiTab-root': {
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: { xs: '0.8rem', sm: '0.9rem', md: '0.95rem' },
                  minHeight: { xs: 48, sm: 56, md: 60 },
                  textTransform: 'none',
                  px: { xs: 1.5, sm: 2, md: 3 },
                  py: { xs: 1, sm: 1.5, md: 2 },
                  minWidth: { xs: 'auto', sm: 120 },
                  maxWidth: { xs: 200, sm: 'none' },
                  '&.Mui-selected': {
                    color: 'primary.main',
                    fontWeight: 600,
                  },
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'rgba(92, 184, 92, 0.04)',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'primary.main',
                  height: 3,
                  borderRadius: '2px 2px 0 0',
                },
                '& .MuiTabs-scrollButtons': {
                  '&.Mui-disabled': {
                    opacity: 0.3,
                  },
                },
              }}
            >
              <Tab label={isMobile ? "👤" : "👤 Profile"} />
              <Tab label={isMobile ? "🔐" : "🔐 Password"} />
              {isAdmin && <Tab label={isMobile ? "👥" : "👥 Users"} />}
              {isAdmin && <Tab label={isMobile ? "📂" : "📂 Categories"} />}
              {isAdmin && <Tab label={isMobile ? "📋" : "📋 Audit Logs"} />}
              {isManager && <Tab label={isMobile ? "🔓" : "🔓 Unlock Requests"} />}
              {isManager && <Tab label={isMobile ? "🔔" : "🔔 Notifications"} />}
              {isAdmin && <Tab label={isMobile ? "📸" : "📸 Snapshots"} />}
              {isAdmin && <Tab label={isMobile ? "🔍" : "🔍 Data Integrity"} />}
            </Tabs>
          </Box>
          
          {/* Tab Content */}
          <Box sx={{ 
            p: { xs: 2, sm: 3, md: 4, lg: 5 },
            width: '100%',
            overflow: 'hidden'
          }}>
            {activeTab === 0 && (
              <Box sx={{ width: '100%' }}>
                <SectionHeader 
                  icon="👤"
                  title="Profile Information"
                  subtitle="Update your personal information and account details"
                />
                
                <FormSection>
                  <Box component="form" onSubmit={handleProfileSubmit} sx={{ width: '100%' }}>
                    <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ width: '100%', m: 0 }}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="First Name"
                          name="first_name"
                          value={profileData.first_name}
                          onChange={handleProfileChange}
                          required
                          variant="outlined"
                          size="medium"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              fontSize: '1rem',
                              height: '56px',
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                              }
                            },
                            '& .MuiInputLabel-root': {
                              fontWeight: 500,
                              fontSize: '1rem',
                            },
                            '& .MuiOutlinedInput-input': {
                              fontSize: '1rem',
                              lineHeight: 1.5
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Last Name"
                          name="last_name"
                          value={profileData.last_name}
                          onChange={handleProfileChange}
                          required
                          variant="outlined"
                          size="medium"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              fontSize: '1rem',
                              height: '56px',
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                              }
                            },
                            '& .MuiInputLabel-root': {
                              fontWeight: 500,
                              fontSize: '1rem',
                            },
                            '& .MuiOutlinedInput-input': {
                              fontSize: '1rem',
                              lineHeight: 1.5
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Email Address"
                          name="email"
                          type="email"
                          value={profileData.email}
                          onChange={handleProfileChange}
                          required
                          disabled
                          variant="outlined"
                          size="medium"
                          helperText="Email cannot be changed"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              fontSize: '1rem',
                              height: '56px',
                              backgroundColor: 'rgba(0, 0, 0, 0.03)',
                            },
                            '& .MuiInputLabel-root': {
                              fontWeight: 500,
                              fontSize: '1rem',
                            },
                            '& .MuiOutlinedInput-input': {
                              fontSize: '1rem',
                              lineHeight: 1.5
                            },
                            '& .MuiFormHelperText-root': {
                              fontSize: '0.875rem',
                              mt: 1
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                    
                    <Divider sx={{ my: { xs: 3, md: 4 } }} />
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'flex-end', 
                      alignItems: 'center',
                      gap: 2,
                      pt: 2
                    }}>
                      <Button 
                        type="submit" 
                        variant="contained"
                        color="primary" 
                        size="large"
                        sx={{ 
                          px: 4, 
                          py: 1.5,
                          fontWeight: 600,
                          borderRadius: 1.5,
                          textTransform: 'none',
                          fontSize: '1rem',
                          lineHeight: 1.5,
                          minWidth: 160,
                          height: 48
                        }}
                        disabled={loadingState}
                      >
                        {loadingState ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={18} color="inherit" />
                            <span>Updating...</span>
                          </Box>
                        ) : (
                          'Update Profile'
                        )}
                      </Button>
                    </Box>
                  </Box>
                </FormSection>
              </Box>
            )}

            {activeTab === 1 && (
              <Box sx={{ width: '100%' }}>
                <SectionHeader 
                  icon="🔐"
                  title="Change Password"
                  subtitle="Update your password to keep your account secure"
                />
                
                <FormSection>
                  <Box component="form" onSubmit={handlePasswordSubmit} sx={{ width: '100%' }}>
                    <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ width: '100%', m: 0 }}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Current Password"
                          name="current_password"
                          type="password"
                          value={passwordData.current_password}
                          onChange={handlePasswordChange}
                          required
                          variant="outlined"
                          size="medium"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              fontSize: '1rem',
                              height: '56px',
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                              }
                            },
                            '& .MuiInputLabel-root': {
                              fontWeight: 500,
                              fontSize: '1rem',
                            },
                            '& .MuiOutlinedInput-input': {
                              fontSize: '1rem',
                              lineHeight: 1.5
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="New Password"
                          name="new_password"
                          type="password"
                          value={passwordData.new_password}
                          onChange={handlePasswordChange}
                          required
                          variant="outlined"
                          size="medium"
                          helperText="Password must be at least 8 characters long"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              fontSize: '1rem',
                              height: '56px',
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                              }
                            },
                            '& .MuiInputLabel-root': {
                              fontWeight: 500,
                              fontSize: '1rem',
                            },
                            '& .MuiOutlinedInput-input': {
                              fontSize: '1rem',
                              lineHeight: 1.5
                            },
                            '& .MuiFormHelperText-root': {
                              fontSize: '0.875rem',
                              mt: 1
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Confirm New Password"
                          name="re_new_password"
                          type="password"
                          value={passwordData.re_new_password}
                          onChange={handlePasswordChange}
                          required
                          variant="outlined"
                          size="medium"
                          error={passwordData.new_password !== '' && passwordData.re_new_password !== '' && passwordData.new_password !== passwordData.re_new_password}
                          helperText={
                            passwordData.new_password !== '' && passwordData.re_new_password !== '' && passwordData.new_password !== passwordData.re_new_password 
                              ? 'Passwords do not match' 
                              : 'Re-enter your new password'
                          }
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              fontSize: '1rem',
                              height: '56px',
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                              }
                            },
                            '& .MuiInputLabel-root': {
                              fontWeight: 500,
                              fontSize: '1rem',
                            },
                            '& .MuiOutlinedInput-input': {
                              fontSize: '1rem',
                              lineHeight: 1.5
                            },
                            '& .MuiFormHelperText-root': {
                              fontSize: '0.875rem',
                              mt: 1
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                    
                    <Divider sx={{ my: { xs: 3, md: 4 } }} />
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'flex-end', 
                      alignItems: 'center',
                      gap: 2,
                      pt: 2
                    }}>
                      <Button 
                        type="submit" 
                        variant="contained"
                        color="secondary" 
                        size="large"
                        sx={{ 
                          px: 4, 
                          py: 1.5,
                          fontWeight: 600,
                          borderRadius: 1.5,
                          textTransform: 'none',
                          fontSize: '1rem',
                          lineHeight: 1.5,
                          minWidth: 160,
                          height: 48
                        }}
                        disabled={loadingState}
                      >
                        {loadingState ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={18} color="inherit" />
                            <span>Updating...</span>
                          </Box>
                        ) : (
                          'Update Password'
                        )}
                      </Button>
                    </Box>
                  </Box>
                </FormSection>
              </Box>
            )}

            {isAdmin && activeTab === 2 && (
              <Box sx={{ width: '100%' }}>
                <SectionHeader 
                  icon="👥"
                  title="User Management"
                  subtitle="Manage user accounts, roles, and permissions"
                />
                
                <FormSection>
                  <UserManagementPanel />
                </FormSection>
              </Box>
            )}

            {isAdmin && activeTab === 3 && (
              <Box sx={{ width: '100%' }}>
                <SectionHeader 
                  icon="📂"
                  title="Category Management"
                  subtitle="Organize and manage system categories"
                />
                
                <FormSection>
                  <CategoryManagementPanel showSnackbar={showSnackbar} />
                </FormSection>
              </Box>
            )}

            {isAdmin && activeTab === 4 && (
              <Box sx={{ width: '100%' }}>
                <SectionHeader 
                  icon="📋"
                  title="Audit Logs"
                  subtitle="View system activity and user actions"
                />
                
                <FormSection>
                  <AuditLogPanel />
                </FormSection>
              </Box>
            )}
            
            {isManager && activeTab === 5 && (
              <Box sx={{ width: '100%' }}>
                <SectionHeader 
                  icon="🔓"
                  title="Unlock Request Management"
                  subtitle="Manage requests to unlock locked rows in the Excel-like view"
                />
                
                <FormSection>
                  <AdminUnlockRequestsPanel />
                </FormSection>
              </Box>
            )}
            
            {isManager && activeTab === 6 && (
              <Box sx={{ width: '100%' }}>
                <SectionHeader 
                  icon="🔔"
                  title="Notifications"
                  subtitle="Manage system notifications and alerts"
                />
                
                <FormSection>
                  <NotificationPanel />
                </FormSection>
              </Box>
            )}
            
            {isAdmin && activeTab === 7 && (
              <Box sx={{ width: '100%' }}>
                <SectionHeader 
                  icon="📸"
                  title="Snapshot Management"
                  subtitle="Manage system snapshots for data backup and historical analysis"
                />
                
                <FormSection>
                  <SnapshotManagementPanel />
                </FormSection>
              </Box>
            )}
            
            {isAdmin && activeTab === 8 && (
              <Box sx={{ width: '100%' }}>
                <SectionHeader 
                  icon="🔍"
                  title="Data Integrity & Consistency"
                  subtitle="Check data consistency between frontend, backend, and API endpoints"
                />
                
                <FormSection>
                  <DataIntegrityPanel />
                </FormSection>
              </Box>
            )}
          </Box>
        </Paper>
        
        {/* Alert Messages */}
        {errorMessage && (
          <Alert severity="error" sx={{ 
            mt: 3, 
            mx: 0,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }} onClose={handleCloseSnackbar}>
            {errorMessage}
          </Alert>
        )}
        
        {successMessage && (
          <Alert severity="success" sx={{ 
            mt: 3, 
            mx: 0,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }} onClose={handleCloseSnackbar}>
            {successMessage}
          </Alert>
        )}
      </Container>
    </Box>
    </>
  );
};

export default Settings;